import React, { useState, useEffect } from "react";
import { Check, AlertTriangle, Upload, ChevronRight, ChevronLeft,
         Home, ExternalLink, X, BedDouble, Users, IndianRupee, Flame, Tag,
         Star, Wifi, Wind, Tv, Car, Droplets, Coffee, MessageSquare } from "lucide-react";
import AvailabilityCalendar from "../components/AvailabilityCalendar";
import { useSettings } from "../context/SettingsContext";
import { toLocalISODate } from "../utils/date";
import { blockedLabel } from "../utils/otaSource";
import { calcRoomTotal, pricePerNightForGuests } from "../utils/pricing";
import { useRazorpayScript, RAZORPAY_BLOCKED_MESSAGE } from "../utils/razorpay";
import { shrinkPhoto, readJson } from "../utils/uploadPhotos";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const BE  = process.env.REACT_APP_BACKEND_URL || "";

const AMENITY_ICONS = { Wifi, AC: Wind, "Hot Water": Droplets, TV: Tv, Parking: Car, Kitchen: Coffee };

// Unmarried Couple booking age policy: 18+ for everyone. Mirrors
// minAgeForGender in backend/routes/api.js — keep the two in step.
const UNMARRIED_COUPLE_MIN_AGE = 18;
function minAgeForGender(_gender) {
  return UNMARRIED_COUPLE_MIN_AGE;
}

function AgeModal({ onConfirm, onCancel }) {
  const { hotelName } = useSettings();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white/70 backdrop-blur-2xl border border-white/50 rounded-3xl shadow-2xl w-full max-w-sm p-7 text-center">
        <div className="w-16 h-16 bg-amber-50/70 backdrop-blur-md rounded-2xl flex items-center justify-center mx-auto mb-4">
          <AlertTriangle size={30} className="text-amber-500" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Age Policy</h3>
        <p className="text-sm text-gray-500 leading-relaxed mb-6">
          Guests must meet the minimum age — <strong>{UNMARRIED_COUPLE_MIN_AGE} years or above for
          all guests</strong>. By proceeding, you confirm compliance. {hotelName} reserves
          the right to deny check-in if age cannot be verified.
        </p>
        <div className="flex gap-3">
          <button onClick={onCancel}
            className="flex-1 py-3 rounded-2xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={onConfirm}
            className="flex-1 py-3 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500
              text-white text-sm font-semibold shadow-md">
            I Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

function StepBar({ current }) {
  const steps = ["Room & Dates", "Guest Info", "Review"];
  return (
    <div className="flex items-center justify-center mb-8">
      {steps.map((label, i) => (
        <React.Fragment key={label}>
          <div className="flex flex-col items-center">
            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold
              transition-all duration-300
              ${i < current  ? "bg-green-500 text-white shadow-md"
              : i === current ? "bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-lg shadow-amber-200"
              : "bg-gray-100 text-gray-400"}`}>
              {i < current ? <Check size={16} /> : i + 1}
            </div>
            <span className={`text-[10px] mt-1.5 font-semibold whitespace-nowrap
              ${i === current ? "text-amber-600" : "text-gray-400"}`}>{label}</span>
          </div>
          {i < steps.length - 1 && (
            <div className={`h-0.5 flex-1 mx-2 mb-4 rounded-full transition-all duration-500
              ${i < current ? "bg-green-400" : "bg-gray-200"}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function PublicBooking() {
  const { hotelName, phone, address, crossPromoName, crossPromoUrl, guestPolicyText, customFields, idProofUploadEnabled, payments } = useSettings();
  // Pay-later off + a gateway configured = the guest pays before the
  // request is accepted at all (and the booking is auto-confirmed on
  // payment — see POST /bookings/direct).
  const mustPayToBook = !!payments?.enabled && payments?.payLaterEnabled === false;
  const { ready: razorpayReady, failed: razorpayFailed } = useRazorpayScript(mustPayToBook);
  const urlP = new URLSearchParams(window.location.search);
  const [step, setStep]             = useState(0);
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [rooms, setRooms]           = useState([]);
  const [bookingDisabled, setBookingDisabled] = useState(false);
  const [availableRooms, setAvail]  = useState(null); // null = not checked yet
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [showAgeModal, setShowAgeModal] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [bookingRef, setBookingRef] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState("");
  const [idFiles, setIdFiles]       = useState([]);   // multi file
  const [roomsLeft, setRoomsLeft]   = useState(null);
  const [promoInput, setPromoInput] = useState("");
  const [promoStatus, setPromoStatus] = useState(null); // { valid, error, discount }
  const [checkingPromo, setCheckingPromo] = useState(false);

  const [form, setForm] = useState({
    checkIn:         urlP.get("checkIn")  || "",
    checkOut:        urlP.get("checkOut") || "",
    guestCount:      1,
    // Per-room guest counts — only actually used once roomCount > 1;
    // seeded from guestCount whenever a room or the room count changes.
    // A group booking several rooms rarely splits evenly, so pricing every
    // room as if it held the same headcount was exactly the "confusion
    // calculating the total" this exists to remove.
    guestCounts:     [1],
    bookingType:     "Standard",
    roomId:          urlP.get("roomId")   || "",
    roomCount:       1,
    fullNames:       [""],
    ages:            [""],
    genders:         [""],
    whatsapp:        "",
    email:           "",
    travelFrom:      "",
    travelTo:        "",
    purposeOfVisit:  "",
    specialRequests: "",
    ageConfirmed:    false,
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const nights = form.checkIn && form.checkOut
    ? Math.max(0, Math.ceil((new Date(form.checkOut) - new Date(form.checkIn)) / 86400000))
    : 0;
  // Same-day (checkIn === checkOut) is a valid hourly-use stay — it bills
  // as 1 night (see calcRoomTotal/backend parseAndValidateDateRange), it
  // just isn't a genuine multi-night stay.
  const sameDayStay = form.checkIn && form.checkOut && form.checkIn === form.checkOut;
  const datesChosen = form.checkIn && form.checkOut && (nights >= 1 || sameDayStay);

  // Active per-room guest counts, always exactly `roomCount` long.
  const activeGuestCounts = form.roomCount > 1
    ? Array.from({ length: form.roomCount }, (_, i) => form.guestCounts[i] || form.guestCount)
    : [form.guestCount];
  const totalGuests = activeGuestCounts.reduce((s, n) => s + n, 0);
  // One ID per guest when the upload is on, up to what one booking can carry
  // (the server's own limit, BOOKING_MAX_ID_FILES in routes/api.js).
  const MAX_ID_FILES = 20;
  const idsNeeded = Math.min(Math.max(1, totalGuests), MAX_ID_FILES);

  // Sums each room's own total instead of one room's total × roomCount —
  // rooms in the same group can have different occupancy, so their
  // extra-person charges (if any) can genuinely differ per room.
  function multiRoomTotal(room) {
    if (!room) return 0;
    return activeGuestCounts.reduce((sum, g) => sum + calcRoomTotal(room, g, form.checkIn, form.checkOut), 0);
  }

  // Check room availability when dates are selected
  useEffect(() => {
    if (!datesChosen) { setAvail(null); setRoomsLeft(null); return; }
    setLoadingRooms(true);
    fetch(`${API}/rooms/availability?checkIn=${form.checkIn}&checkOut=${form.checkOut}`)
      .then(r => r.json())
      .then(j => { setAvail(j.data || []); setRooms(j.data || []); setRoomsLeft(j.roomsLeft ?? null); })
      .catch(() => setAvail([]))
      .finally(() => setLoadingRooms(false));
  }, [form.checkIn, form.checkOut]);

  /* The room the guest picked on the landing page ("Book" on a room card,
     or the room page), carried here as ?roomId=. Kept apart from
     form.roomId, which changing dates clears: once availability for the new
     dates is in, the room is selected again if it is free there — and
     dropped if it isn't, so a room the guest can't have is never left
     selected. A plain "Book Now" has no room and behaves exactly as before. */
  const [focusRoomId] = useState(urlP.get("roomId") || "");
  const [showAllRooms, setShowAllRooms] = useState(false);
  useEffect(() => {
    if (!focusRoomId || !availableRooms) return;
    const free = availableRooms.some(r => r._id === focusRoomId && r.available);
    if (free && !form.roomId) setForm(f => ({ ...f, roomId: focusRoomId, roomCount: 1 }));
    if (!free && form.roomId === focusRoomId) setForm(f => ({ ...f, roomId: "", roomCount: 1 }));
  }, [availableRooms]);

  // A previously-applied promo's discount was computed against whatever
  // dates/room/guest-count were selected at that moment — if the guest goes
  // back and changes any of those, the on-screen discount would otherwise
  // stay stale (still correct server-side at submit time, but misleading
  // here). Clear it so the guest has to re-apply against the new selection.
  useEffect(() => {
    setPromoStatus(null);
  }, [form.checkIn, form.checkOut, form.roomId, form.guestCount, form.roomCount, form.guestCounts]);

  async function applyPromo() {
    if (!promoInput.trim()) return;
    setCheckingPromo(true); setPromoStatus(null);
    try {
      const res  = await fetch(`${API}/promo-codes/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: promoInput.trim(), checkIn: form.checkIn, checkOut: form.checkOut }),
      });
      const json = await res.json();
      if (!json.success) { setPromoStatus({ valid: false, error: json.error }); return; }
      const room = (availableRooms || rooms)?.find(r => r._id === form.roomId);
      const base = multiRoomTotal(room);
      const discount = json.data.type === "Percent" ? Math.round(base * json.data.value / 100) : Math.min(json.data.value, base);
      setPromoStatus({ valid: true, ...json.data, discount });
    } catch { setPromoStatus({ valid: false, error: "Could not validate code" }); }
    finally { setCheckingPromo(false); }
  }

  // Load all rooms on mount for display
  useEffect(() => {
    fetch(`${API}/rooms/public`)
      .then(r => {
        if (r.status === 404) { setBookingDisabled(true); return { data: [] }; }
        return r.json();
      })
      .then(j => setRooms(j.data || []))
      .catch(() => {});
  }, []);

  function validateStep0() {
    if (!datesChosen) { setError("Please select valid dates."); return false; }
    if (!form.roomId) { setError("Please select a room to continue. A room must be selected to prevent double-bookings."); return false; }
    const selectedRoom = availableRooms?.find(r => r._id === form.roomId);
    if (selectedRoom && !selectedRoom.available) { setError("This room is not available for your selected dates. Please choose another."); return false; }
    if (selectedRoom && form.roomCount > selectedRoom.availableCount) {
      setError(`Only ${selectedRoom.availableCount} room(s) of this type are available for these dates.`); return false;
    }
    if (form.bookingType === "Unmarried Couple" && !form.ageConfirmed) {
      setShowAgeModal(true); return false;
    }
    return true;
  }

  function validateStep1() {
    const named = form.fullNames.slice(0, Math.max(1, totalGuests)).filter(n => n?.trim()).length;
    if (named < Math.max(1, totalGuests)) {
      setError(`Please enter the full name of all ${Math.max(1, totalGuests)} guests.`);
      return false;
    }
    if (form.bookingType === "Unmarried Couple") {
      const n = Math.max(1, totalGuests);
      const guestAges    = Array.from({ length: n }, (_, i) => Number(form.ages[i]));
      const guestGenders = Array.from({ length: n }, (_, i) => form.genders[i]);
      if (guestGenders.some(g => g !== "Male" && g !== "Female")) {
        setError("Please select each guest's gender for an Unmarried Couple booking.");
        return false;
      }
      const invalid = guestAges.some((a, i) => !Number.isFinite(a) || a < minAgeForGender(guestGenders[i]));
      if (invalid) {
        setError(`Guests must meet the minimum age for an Unmarried Couple booking — ${UNMARRIED_COUPLE_MIN_AGE}+ for all guests.`);
        return false;
      }
    }
    const digits = form.whatsapp.replace(/\D/g, "");
    if (!form.whatsapp) { setError("WhatsApp number is required."); return false; }
    if (digits.length < 7 || digits.length > 15) { setError("Enter a valid WhatsApp number (digits only, with country code if needed)."); return false; }
    // Required rather than optional: the confirmation, the invoice PDF and
    // any payment receipt are all delivered by email, and a booking with no
    // address leaves the guest with nothing to keep. Deliberately a loose
    // shape check — the server applies the same one, and over-strict email
    // regexes reject valid addresses.
    const emailValue = form.email.trim();
    if (!emailValue) { setError("Email address is required — we'll send your confirmation and invoice there."); return false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) { setError("Enter a valid email address."); return false; }
    for (const f of customFields) {
      if (f.required && !String(customFieldValues[f.key] || "").trim()) {
        setError(`"${f.label}" is required.`);
        return false;
      }
    }
    // Required only when the tenant has the upload switched on — when it's
    // off the field isn't rendered at all, so demanding it would make the
    // form impossible to submit. The server applies the same rule.
    if (idProofUploadEnabled && idFiles.length < idsNeeded) {
      setError(`Please upload an identity proof for each guest — ${idsNeeded} needed, ${idFiles.length} added.`);
      return false;
    }
    return true;
  }

  function tryNext() {
    setError("");
    if (step === 0 && !validateStep0()) return;
    if (step === 1 && !validateStep1()) return;
    setStep(s => s + 1);
  }

  const MAX_FILE_BYTES = 10 * 1024 * 1024; // matches backend multer limit (api.js)
  const ALLOWED_FILE_TYPES = ["image/jpeg", "image/png", "application/pdf"];

  async function addFile(e) {
    const picked = Array.from(e.target.files);
    e.target.value = ""; // allow re-selecting the same file after a rejection
    // Photos are shrunk first (PDFs go as they are), so an ID for every guest
    // fits in one booking — and a big phone photo is no longer refused for size.
    // One at a time: a phone names every photo "image.jpg", and several
    // full-size decodes at once can exhaust its memory.
    const newFiles = [];
    for (const f of picked) {
      newFiles.push(ALLOWED_FILE_TYPES.includes(f.type) && f.type.startsWith("image/") ? (await shrinkPhoto(f)).file : f);
    }

    const oversized = newFiles.filter(f => f.size > MAX_FILE_BYTES);
    const wrongType = newFiles.filter(f => f.size <= MAX_FILE_BYTES && !ALLOWED_FILE_TYPES.includes(f.type));
    if (oversized.length) {
      setError(`${oversized.map(f => f.name).join(", ")} is over 10MB — please upload a smaller file.`);
    } else if (wrongType.length) {
      setError(`${wrongType.map(f => f.name).join(", ")}: unsupported file type. Please upload JPG, PNG, or PDF only.`);
    } else {
      setError("");
    }

    const accepted = newFiles.filter(f => f.size <= MAX_FILE_BYTES && ALLOWED_FILE_TYPES.includes(f.type));
    setIdFiles(prev => [...prev, ...accepted].slice(0, MAX_ID_FILES));
  }

  function removeFile(i) {
    setIdFiles(prev => prev.filter((_, j) => j !== i));
  }

  /* ── Pay-before-booking. When the tenant has turned pay-later off, the
     booking request isn't accepted at all until it's paid — the server
     enforces the same rule (POST /bookings/direct), this just makes it
     possible to satisfy. The amount comes from the server, never from this
     page's own price arithmetic: what the guest is charged and what the
     booking records have to be the same number. ── */
  async function payThenSubmit() {
    if (razorpayFailed) { setError(RAZORPAY_BLOCKED_MESSAGE); return; }
    if (!razorpayReady) { setError("Payment is still loading — please try again in a moment."); return; }
    setSubmitting(true); setError("");
    try {
      const orderRes = await fetch(`${API}/payment/booking-order`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        // The guest's details go up WITH the order, not just with the
        // booking afterwards. The server records them against the Razorpay
        // order id, so a payment that gets captured while this page is gone
        // — the normal mobile case, where a UPI hop can have the OS evict
        // the tab before `handler` ever runs — can still be traced to who
        // was booking what. Without this the payment is unreconcilable.
        // ID documents are deliberately not included: they are optional,
        // and a file upload here would slow down opening Checkout.
        body: JSON.stringify({
          roomId: form.roomId, checkIn: form.checkIn, checkOut: form.checkOut,
          guestCounts: activeGuestCounts, guestCount: form.guestCount,
          roomCount: form.roomCount,
          ...(promoStatus?.valid && { promoCode: promoStatus.code }),
          fullNames: form.fullNames, whatsapp: form.whatsapp, email: form.email,
          travelFrom: form.travelFrom, travelTo: form.travelTo,
          purposeOfVisit: form.purposeOfVisit, bookingType: form.bookingType,
          specialRequests: form.specialRequests,
          ages: form.ages, genders: form.genders,
        }),
      });
      const orderJson = await orderRes.json();
      if (!orderJson.success) throw new Error(orderJson.error);

      const rzp = new window.Razorpay({
        key: orderJson.data.keyId,
        order_id: orderJson.data.orderId,
        amount: orderJson.data.amountPaise,
        currency: orderJson.data.currency,
        name: hotelName,
        description: `Booking at ${hotelName}`,
        prefill: { contact: form.whatsapp, email: form.email, name: form.fullNames[0] || "" },
        // No `redirect`/`callback_url`: Checkout stays a modal on THIS page
        // and returns through `handler` below, so the guest never leaves the
        // site. (Setting redirect mode would POST the result to a callback
        // URL and land the guest on a bare server response instead — the
        // booking is only created here, so that would strand the payment.)
        // Bank/3DS and UPI-app hops happen inside Razorpay's own frame and
        // come back here on their own.
        handler: (response) => {
          // Only NOW is the booking actually submitted — with the payment
          // attached, so a paid guest can never end up with no booking.
          submitBooking({
            razorpay_order_id:   response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature:  response.razorpay_signature,
          });
        },
        modal: {
          ondismiss: () => setSubmitting(false),
          // Closing the modal mid-payment is how a guest ends up charged
          // with no booking on file — the one window where that's possible.
          // Confirm before closing, and don't let a stray Escape do it.
          confirm_close: true,
          escape: false,
        },
        theme: { color: "#F59E0B" },
      });
      rzp.on("payment.failed", () => { setError("Payment failed. Please try again."); setSubmitting(false); });
      rzp.open();
    } catch (e) {
      setError(e.message || "Could not start the payment. Please try again.");
      setSubmitting(false);
    }
  }

  function submit() {
    if (mustPayToBook) return payThenSubmit();
    return submitBooking(null);
  }

  async function submitBooking(paymentProof) {
    setSubmitting(true); setError("");
    try {
      const fd = new FormData();
      if (paymentProof) {
        fd.append("razorpay_order_id",   paymentProof.razorpay_order_id);
        fd.append("razorpay_payment_id", paymentProof.razorpay_payment_id);
        fd.append("razorpay_signature",  paymentProof.razorpay_signature);
      }
      fd.append("checkIn",         form.checkIn);
      fd.append("checkOut",        form.checkOut);
      fd.append("guestCount",      form.guestCount);
      fd.append("bookingType",     form.bookingType);
      fd.append("whatsapp",        form.whatsapp);
      fd.append("email",           form.email);
      fd.append("travelFrom",      form.travelFrom);
      fd.append("travelTo",        form.travelTo);
      fd.append("purposeOfVisit",  form.purposeOfVisit);
      fd.append("specialRequests", form.specialRequests);
      fd.append("ageConfirmed",    form.ageConfirmed);
      fd.append("roomId",          form.roomId);
      fd.append("roomCount",       form.roomCount);
      fd.append("guestCounts",     JSON.stringify(activeGuestCounts));
      fd.append("policyAccepted",  policyAccepted ? "true" : "false");
      fd.append("customFields",    JSON.stringify(customFieldValues));
      if (promoStatus?.valid) fd.append("promoCode", promoStatus.code);
      form.fullNames.slice(0, Math.max(1, totalGuests)).filter(Boolean).forEach(n => fd.append("fullNames", n));
      if (form.bookingType === "Unmarried Couple") {
        form.ages.slice(0, totalGuests).forEach(a => fd.append("ages", a));
        form.genders.slice(0, totalGuests).forEach(g => fd.append("genders", g));
      }
      idFiles.forEach(f => fd.append("identityProofs", f));

      const res  = await fetch(`${API}/bookings/direct`, { method: "POST", body: fd });
      const json = await readJson(res);
      if (!json.success) throw new Error(json.error);
      setBookingRef(json.data.bookings?.map(b => b.bookingRef).join(", ") || json.data.booking.bookingRef);
      setSubmitted(true);
    } catch (e) {
      // Only a plain Error (our own `throw new Error(json.error)` above) is
      // meant for the guest to read — it's the backend's own validation
      // message. Anything else (TypeError, DOMException, ...) is a native
      // browser/network failure whose .message is internal engine wording
      // ("The string did not match the expected pattern.", "Load failed",
      // etc.) that would only confuse a guest. Log those for diagnosis and
      // show a generic, actionable message instead.
      const base = e.constructor === Error
        ? (e.message || "Submission failed. Please try again.")
        : "Something went wrong sending your booking — please check your connection and try again, or call us directly.";
      if (e.constructor !== Error) console.error("Booking submission failed:", e);

      // The one case where a guest is genuinely out of pocket: the payment
      // went through, but creating the booking then failed. Never let that
      // surface as a bare "try again" — retrying would charge them twice,
      // and without the payment reference they have nothing to quote when
      // they call. Show the id and tell them not to pay again.
      setError(paymentProof
        ? `${base}\n\nYour payment DID go through (reference ${paymentProof.razorpay_payment_id}). Please don't pay again — contact us with this reference and we'll confirm your booking manually.`
        : base);
    }
    finally { setSubmitting(false); }
  }

  const selectedRoom = form.roomId
    ? (availableRooms || rooms)?.find(r => r._id === form.roomId)
    : null;

  // With a room chosen on the landing page: just that room when it is free
  // on these dates, otherwise only the rooms that are. "See all rooms"
  // (showAllRooms) brings back the full list, as a plain "Book Now" shows it.
  const focusRoom = focusRoomId && availableRooms ? availableRooms.find(r => r._id === focusRoomId) : null;
  const focusing = !!focusRoom && !showAllRooms;
  const focusFree = focusing && focusRoom.available;
  const shownRooms = !focusing ? availableRooms
    : focusFree ? [focusRoom]
    : availableRooms.filter(r => r.available && r._id !== focusRoomId);

  // How many guests this booking may actually hold. The Guests dropdown used
  // to be a hardcoded 1–8 regardless of the room, so a guest could ask for
  // more people than the room sleeps and only find out (if at all) after
  // submitting. Capped at the chosen room's own capacity; before a room is
  // picked, at the largest room on offer, so the option is never one no room
  // could honour. Math.max's leading 1 also covers the empty-list case,
  // where a bare spread would give -Infinity.
  const maxGuests = selectedRoom?.capacity
    || Math.max(1, ...((availableRooms || rooms || []).map(r => r.capacity || 1)));

  // Picking a smaller room after choosing a larger party would otherwise
  // leave guestCount above what that room allows — clamp it (and the
  // per-room counts a group booking keeps) down to fit.
  useEffect(() => {
    setForm(f => {
      if (f.guestCount <= maxGuests && f.guestCounts.every(n => n <= maxGuests)) return f;
      return {
        ...f,
        guestCount: Math.min(f.guestCount, maxGuests),
        guestCounts: f.guestCounts.map(n => Math.min(n, maxGuests)),
      };
    });
  }, [maxGuests]);

  /* ── Booking disabled by the tenant (directBookingPage feature off) ── */
  if (bookingDisabled) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-amber-50/30 to-orange-50/20 flex items-center justify-center p-6">
        <div className="bg-white/60 backdrop-blur-2xl rounded-3xl shadow-2xl shadow-amber-900/5 border border-white/50 p-10 text-center max-w-sm w-full">
          <h2 className="text-2xl font-black text-gray-900 mb-2">Online Booking Unavailable</h2>
          <p className="text-sm text-gray-500 mb-5 leading-relaxed">
            {hotelName} isn't taking bookings through this page right now — please contact us directly.
          </p>
          {phone && (
            <a href={`https://wa.me/${String(phone).replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-3 rounded-2xl mb-3
                bg-green-50 border border-green-200 text-green-700 font-medium text-sm hover:bg-green-100">
              💬 WhatsApp Us
            </a>
          )}
          <a href="/"
            className="flex items-center justify-center gap-2 w-full py-3.5 rounded-2xl
              bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold text-sm
              hover:from-amber-600 hover:to-orange-600 shadow-md transition-all">
            ← Back to {hotelName}
          </a>
        </div>
      </div>
    );
  }

  /* ── Success screen ── */
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 flex items-center justify-center p-6">
        <div className="bg-white/60 backdrop-blur-2xl rounded-3xl shadow-2xl shadow-green-900/5 border border-white/50 p-10 text-center max-w-sm w-full">
          <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500
            rounded-full flex items-center justify-center mx-auto mb-5 shadow-xl shadow-green-200">
            <Check size={38} className="text-white" />
          </div>
          <h2 className="text-2xl font-black text-gray-900 mb-2">Booking Received!</h2>
          {/* Email is now a required field on this form and the invoice is
              sent there, so pointing the guest only at WhatsApp understated
              where their confirmation actually lands. */}
          <p className="text-sm text-gray-500 mb-5 leading-relaxed">
            We'll confirm your booking shortly.
            Check your WhatsApp and email{form.email ? <> (<span className="text-gray-600">{form.email}</span>)</> : ""} for details!
          </p>
          <p className="text-xs text-gray-400 mt-1">
            📍 {address}
          </p>
          <div className="bg-gradient-to-br from-amber-50/70 to-orange-50/70 backdrop-blur-md border border-amber-200/60
            rounded-2xl px-5 py-4 mb-6">
            <p className="text-xs text-amber-600 font-semibold uppercase tracking-wider mb-1">
              {bookingRef.includes(",") ? "Your References" : "Your Reference"}
            </p>
            <p className="font-mono font-black text-amber-800" style={{ fontSize: bookingRef.includes(",") ? "1rem" : "1.25rem" }}>
              {bookingRef}
            </p>
          </div>
          <div className="space-y-3">
            <a href="/"
              className="flex items-center justify-center gap-2 w-full py-3.5 rounded-2xl
                bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold text-sm
                hover:from-amber-600 hover:to-orange-600 shadow-md transition-all">
              ← Back to {hotelName}
            </a>
            <a href={`https://wa.me/${String(phone).replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-3 rounded-2xl
                bg-green-50 border border-green-200 text-green-700 font-medium text-sm hover:bg-green-100">
              💬 WhatsApp Us
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-amber-50/30 to-orange-50/20">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-white/60 backdrop-blur-xl border-b border-white/40 shadow-sm">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="tap-44 flex items-center gap-2.5 hover:opacity-80 transition-opacity">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500
              flex items-center justify-center shadow-md">
              <Home size={17} className="text-white" />
            </div>
            <div>
              <p className="font-black text-gray-900 text-sm">{hotelName}</p>
            </div>
          </a>
          <a href={`tel:${String(phone).replace(/\D/g, "")}`} className="text-xs font-semibold text-amber-700
            bg-amber-50/60 backdrop-blur-md border border-amber-200/60 px-3 py-1.5 rounded-full hover:bg-amber-100/70 transition-colors tap-44">
            📞 {phone}
          </a>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-black text-gray-900 mb-1">Book Your Stay</h1>
          <p className="text-gray-500 text-sm">Warm hospitality in the heart of {address}</p>
        </div>

        <div className="bg-white/55 backdrop-blur-2xl rounded-3xl shadow-2xl shadow-amber-900/5 border border-white/50 p-4 sm:p-6 md:p-8">
          <StepBar current={step} />

          {/* ── STEP 0: Room & Dates ── */}
          {step === 0 && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                    Check-in *
                  </label>
                  <input type="date" value={form.checkIn}
                    min={toLocalISODate(new Date())}
                    onChange={e => { set("checkIn", e.target.value); set("roomId", ""); set("roomCount", 1); }}
                    className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm
                      focus:outline-none focus:ring-2 focus:ring-amber-400" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                    Check-out *
                  </label>
                  <input type="date" value={form.checkOut}
                    min={form.checkIn || toLocalISODate(new Date())}
                    onChange={e => { set("checkOut", e.target.value); set("roomId", ""); set("roomCount", 1); }}
                    className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm
                      focus:outline-none focus:ring-2 focus:ring-amber-400" />
                </div>
              </div>

              {datesChosen && (
                <div className="bg-gradient-to-r from-amber-50/70 to-orange-50/70 backdrop-blur-md border border-amber-100/70
                  rounded-2xl p-3 flex items-center justify-center gap-2">
                  <Star size={14} className="text-amber-600" />
                  <span className="text-sm font-bold text-amber-700">
                    {sameDayStay ? "Same-day Stay Selected" : `${nights} Night${nights > 1 ? "s" : ""} Selected`}
                  </span>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                  Or pick dates on the live calendar
                </label>
                <AvailabilityCalendar checkIn={form.checkIn} checkOut={form.checkOut}
                  onChange={(ci, co) => { set("checkIn", ci); set("checkOut", co); set("roomId", ""); set("roomCount", 1); }} />
              </div>

              {roomsLeft !== null && roomsLeft > 0 && roomsLeft <= 2 && (
                <div className="flex items-center gap-2 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">
                  <Flame size={14} className="text-rose-500" />
                  <span className="text-xs font-bold text-rose-600">
                    Only {roomsLeft} room{roomsLeft > 1 ? "s" : ""} left for these dates
                  </span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                    Guests
                  </label>
                  <select value={form.guestCount} onChange={e => set("guestCount", parseInt(e.target.value))}
                    className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm
                      focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white">
                    {Array.from({ length: maxGuests }, (_, i) => i + 1).map(n => (
                      <option key={n} value={n}>{n} Guest{n > 1 ? "s" : ""}</option>
                    ))}
                  </select>
                  {selectedRoom && (
                    <p className="text-[11px] text-gray-400 mt-1">
                      {selectedRoom.name} sleeps up to {maxGuests}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                    Type
                  </label>
                  <select value={form.bookingType} onChange={e => set("bookingType", e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm
                      focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white">
                    {["Standard","Family","Group","Corporate","Unmarried Couple"].map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>

              {/* ── ROOM SELECTION (MANDATORY) ── */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                  Select Room * {!form.checkIn || !form.checkOut
                    ? <span className="text-gray-300 font-normal normal-case">(enter dates first)</span>
                    : ""}
                </label>

                {loadingRooms && (
                  <div className="flex items-center gap-2 py-4 text-sm text-gray-400">
                    <div className="w-4 h-4 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
                    Checking availability…
                  </div>
                )}

                {/* No rooms configured yet */}
                {!loadingRooms && availableRooms !== null && availableRooms.length === 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-center">
                    <p className="text-sm text-red-600 font-medium">No rooms available for these dates.</p>
                    <p className="text-xs text-red-400 mt-1">Please try different dates or contact us.</p>
                  </div>
                )}

                {!loadingRooms && rooms.length === 0 && availableRooms === null && (
                  <div className="bg-amber-50/60 backdrop-blur-md border border-amber-200/60 rounded-2xl p-4 text-center">
                    <p className="text-sm text-amber-700 font-medium">Enter check-in and check-out dates to see available rooms.</p>
                  </div>
                )}

                {/* Room cards */}
                {!loadingRooms && availableRooms && availableRooms.length > 0 && focusing && (
                  <div className={`mb-3 rounded-2xl border px-3 py-2.5 flex items-center justify-between gap-3 flex-wrap ${
                    focusFree ? "bg-amber-50/60 border-amber-100" : "bg-rose-50 border-rose-200"}`}>
                    <p className={`text-xs ${focusFree ? "text-amber-800" : "text-rose-700"}`}>
                      {focusFree
                        ? <>Showing <strong>{focusRoom.name}</strong>, the room you chose.</>
                        : shownRooms.length
                          ? <><strong>{focusRoom.name}</strong> isn't available on these dates. These rooms are:</>
                          : <><strong>{focusRoom.name}</strong> isn't available on these dates, and no other room is free either. Please try different dates.</>}
                    </p>
                    {focusFree && availableRooms.length > 1 && (
                      <button type="button" onClick={() => setShowAllRooms(true)}
                        className="min-h-[44px] sm:min-h-0 text-xs font-bold text-amber-700 hover:text-amber-900 underline underline-offset-2">
                        See all rooms
                      </button>
                    )}
                  </div>
                )}

                {!loadingRooms && availableRooms && availableRooms.length > 0 && (
                  <div className="space-y-3">
                    {shownRooms.map(room => {
                      const isSelected  = form.roomId === room._id;
                      const unavailable = !room.available;
                      const imgSrc = room.photos?.[0]
                        ? (room.photos[0].startsWith("/uploads") ? `${BE}${room.photos[0]}` : room.photos[0])
                        : null;

                      return (
                        <div key={room._id} role="button" tabIndex={unavailable ? -1 : 0}
                          onClick={() => !unavailable && setForm(f => ({ ...f, roomId: room._id, roomCount: 1 }))}
                          className={`w-full text-left rounded-2xl border-2 p-3 transition-all duration-200
                            ${unavailable
                              ? "opacity-50 cursor-not-allowed border-gray-100 bg-gray-50"
                              : isSelected
                                ? "border-amber-400 bg-amber-50 shadow-md cursor-pointer"
                                : "border-gray-200 hover:border-amber-300 hover:bg-amber-50/30 cursor-pointer"
                            }`}
                        >
                          <div className="flex gap-3">
                            {imgSrc ? (
                              <img src={imgSrc} alt={room.name} loading="lazy"
                                className="w-20 h-16 object-cover rounded-xl flex-shrink-0" />
                            ) : (
                              <div className="w-20 h-16 bg-gradient-to-br from-amber-100 to-orange-100
                                rounded-xl flex items-center justify-center flex-shrink-0">
                                <BedDouble size={22} className="text-amber-400" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <p className="font-bold text-gray-900 text-sm">{room.name}</p>
                                  <p className="text-[11px] text-amber-600 font-medium flex items-center gap-1.5">
                                    {room.type}
                                    {room.totalUnits > 1 && (
                                      <span className={`font-semibold ${room.availableCount === 0 ? "text-red-500" : "text-gray-400"}`}>
                                        · {room.availableCount} of {room.totalUnits} available
                                      </span>
                                    )}
                                  </p>
                                </div>
                                <div className="text-right flex-shrink-0">
                                  {unavailable ? (
                                    <span className="text-[10px] font-bold text-red-500 bg-red-50
                                      px-2 py-0.5 rounded-full">{blockedLabel(room.blockedBy)}</span>
                                  ) : isSelected ? (
                                    <span className="text-[10px] font-bold text-amber-600 bg-amber-100
                                      px-2 py-0.5 rounded-full flex items-center gap-1">
                                      <Check size={10} /> Selected
                                    </span>
                                  ) : null}
                                  {/* Once dates are chosen this MUST reflect them.
                                      It called pricePerNightForGuests without a
                                      date, which returns the room's base rate by
                                      design — so a night carrying a seasonal
                                      override showed the old price here while the
                                      total below correctly used the override. The
                                      guest saw "₹2,000/night" above "₹5,000" for
                                      one night.

                                      Derived from calcRoomTotal (the same function
                                      that produces the total) divided by nights, so
                                      the two can never disagree again. Across nights
                                      priced differently this is a true average, and
                                      it still reconciles. With no dates yet there is
                                      no night to price, so the base stands as a
                                      "from" figure exactly as before. */}
                                  <p className="text-xs font-bold text-gray-900 mt-0.5">
                                    ₹{(nights > 0
                                        ? Math.round(calcRoomTotal(room, form.guestCount, form.checkIn, form.checkOut) / nights)
                                        : pricePerNightForGuests(room, form.guestCount)
                                      ).toLocaleString("en-IN")}
                                    <span className="font-normal text-gray-400">/night</span>
                                  </p>
                                  {room.extraPersonCharge > 0 && form.guestCount > (room.baseOccupancy || room.capacity) && (
                                    <p className="text-[10px] text-gray-400">for {form.guestCount} guests</p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 mt-1.5 text-[11px] text-gray-500 flex-wrap">
                                <Users size={11} /> {room.baseOccupancy || room.capacity} guests
                                {(room.amenities||[]).slice(0,3).map(a => (
                                  <span key={a} className="bg-gray-100 px-1.5 py-0.5 rounded">{a}</span>
                                ))}
                              </div>
                              <div className="flex items-center gap-2 mt-1.5">
                                {room.recentBookings >= 3 && (
                                  <span className="text-[10px] font-bold text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded-full">
                                    🔥 Booked {room.recentBookings}× this month
                                  </span>
                                )}
                                <a href={`/rooms/${room._id}`} target="_blank" rel="noopener noreferrer"
                                  onClick={e => e.stopPropagation()}
                                  className="text-[10px] font-semibold text-gray-400 hover:text-amber-600 underline">
                                  View Details
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* How many rooms of this type — one guest can claim several
                    identical rooms in this same form instead of repeating
                    the whole booking N times. */}
                {selectedRoom && selectedRoom.totalUnits > 1 && (
                  <div className="mt-3 flex items-center justify-between bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
                    <span className="text-sm font-semibold text-gray-700">Number of rooms</span>
                    <select
                      value={form.roomCount}
                      onChange={e => {
                        const n = parseInt(e.target.value);
                        setForm(f => ({
                          ...f,
                          roomCount: n,
                          // Seed each room from its current value (or the
                          // top-level default for a newly added room) so
                          // switching from 2→3 rooms doesn't reset rooms 1-2.
                          guestCounts: Array.from({ length: n }, (_, i) => f.guestCounts[i] || f.guestCount),
                        }));
                      }}
                      className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm font-bold text-gray-900 bg-white
                        focus:outline-none focus:ring-2 focus:ring-amber-400"
                    >
                      {Array.from({ length: selectedRoom.availableCount }, (_, i) => i + 1).map(n => (
                        <option key={n} value={n}>{n} room{n > 1 ? "s" : ""}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Per-room guest counts — a group rarely splits evenly
                    across rooms, so each room's occupancy (and any
                    extra-person charge) is set individually rather than
                    assuming every room holds the same number of guests. */}
                {selectedRoom && form.roomCount > 1 && (
                  <div className="mt-3 bg-gray-50 border border-gray-200 rounded-xl p-3 space-y-2">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Guests per room</p>
                    {activeGuestCounts.map((g, i) => (
                      <div key={i} className="flex items-center justify-between gap-3">
                        <span className="text-sm text-gray-600">Room {i + 1}</span>
                        <select
                          value={g}
                          onChange={e => {
                            const val = parseInt(e.target.value);
                            setForm(f => {
                              const next = [...activeGuestCounts];
                              next[i] = val;
                              return { ...f, guestCounts: next };
                            });
                          }}
                          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm font-medium text-gray-900 bg-white
                            focus:outline-none focus:ring-2 focus:ring-amber-400"
                        >
                          {Array.from({ length: selectedRoom.capacity }, (_, j) => j + 1).map(n => (
                            <option key={n} value={n}>{n} guest{n > 1 ? "s" : ""}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                )}

                {/* Price preview */}
                {selectedRoom && datesChosen && (
                  <div className="mt-3 bg-gradient-to-r from-green-50/70 to-emerald-50/70 backdrop-blur-md
                    border border-green-200/60 rounded-xl p-3 flex justify-between items-center">
                    <span className="text-sm text-green-700">
                      {selectedRoom.name}{form.roomCount > 1 ? ` × ${form.roomCount} rooms` : ""} × {sameDayStay ? "same-day stay" : `${nights} night${nights>1?"s":""}`}
                      <span className="block text-[11px] text-green-600">
                        {form.roomCount > 1
                          ? `${totalGuests} guests total (${activeGuestCounts.join(", ")} per room)`
                          : (selectedRoom.extraPersonCharge > 0 && form.guestCount > (selectedRoom.baseOccupancy || selectedRoom.capacity)
                              ? `for ${form.guestCount} guests`
                              : null)}
                      </span>
                    </span>
                    <span className="text-base font-black text-green-800">
                      ₹{multiRoomTotal(selectedRoom).toLocaleString("en-IN")}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── STEP 1: Guest Info ── */}
          {step === 1 && (
            <div className="space-y-5">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                  Guest Name(s) as per ID *
                </label>
                {form.bookingType === "Unmarried Couple" && (
                  <p className="text-xs text-amber-600 mb-2">
                    Minimum age applies — {UNMARRIED_COUPLE_MIN_AGE}+ for all guests. Select each
                    guest's gender and enter their age.
                  </p>
                )}
                {form.roomCount > 1 && (
                  <p className="text-xs text-gray-400 mb-2">
                    {totalGuests} guests across all {form.roomCount} rooms — enter everyone's name.
                  </p>
                )}
                {Array.from({ length: Math.max(1, totalGuests) }).map((_, i) => (
                  <div key={i} className={`flex gap-2 ${i > 0 ? "mt-2" : ""}`}>
                    <input type="text"
                      value={form.fullNames[i] || ""}
                      onChange={e => {
                        const names = [...form.fullNames];
                        names[i] = e.target.value;
                        set("fullNames", names);
                      }}
                      placeholder={`Guest ${i + 1} full name`}
                      className="flex-1 min-w-0 border border-gray-200 rounded-xl px-4 py-3 text-sm
                        focus:outline-none focus:ring-2 focus:ring-amber-400"
                    />
                    {form.bookingType === "Unmarried Couple" && (
                      <>
                        <select value={form.genders[i] || ""}
                          onChange={e => {
                            const genders = [...form.genders];
                            genders[i] = e.target.value;
                            set("genders", genders);
                          }}
                          className="w-28 border border-gray-200 rounded-xl px-2 py-3 text-sm
                            focus:outline-none focus:ring-2 focus:ring-amber-400">
                          <option value="">Gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                        </select>
                        <input type="number" min={0} inputMode="numeric"
                          value={form.ages[i] || ""}
                          onChange={e => {
                            const ages = [...form.ages];
                            ages[i] = e.target.value;
                            set("ages", ages);
                          }}
                          placeholder="Age"
                          className={`w-20 border rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2
                            ${form.ages[i] && Number(form.ages[i]) < minAgeForGender(form.genders[i])
                              ? "border-rose-300 focus:ring-rose-400 text-rose-600"
                              : "border-gray-200 focus:ring-amber-400"}`}
                        />
                      </>
                    )}
                  </div>
                ))}
              </div>

              <div>
                <label htmlFor="pb-whatsapp" className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                  WhatsApp Number *
                </label>
                <input id="pb-whatsapp" name="whatsapp" type="tel" value={form.whatsapp}
                  onChange={e => set("whatsapp", e.target.value)}
                  placeholder="+91 98765 43210"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm
                    focus:outline-none focus:ring-2 focus:ring-amber-400" />
                <p className="text-xs text-gray-400 mt-1">Booking confirmation will be sent to this number</p>
              </div>

              <div>
                <label htmlFor="pb-email" className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                  Email <span className="text-red-500">*</span>
                </label>
                <input id="pb-email" name="email" type="email" required value={form.email}
                  onChange={e => set("email", e.target.value)}
                  placeholder="you@example.com"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm
                    focus:outline-none focus:ring-2 focus:ring-amber-400" />
                <p className="text-xs text-gray-400 mt-1">We'll send your confirmation and invoice here</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="pb-travel-from" className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                    Travel From
                  </label>
                  <input id="pb-travel-from" name="travelFrom" type="text" value={form.travelFrom}
                    onChange={e => set("travelFrom", e.target.value)}
                    placeholder="e.g. Kolkata"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm
                      focus:outline-none focus:ring-2 focus:ring-amber-400" />
                </div>
                <div>
                  <label htmlFor="pb-travel-to" className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                    Travel To
                  </label>
                  <input id="pb-travel-to" name="travelTo" type="text" value={form.travelTo}
                    onChange={e => set("travelTo", e.target.value)}
                    placeholder="e.g. Guwahati"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm
                      focus:outline-none focus:ring-2 focus:ring-amber-400" />
                </div>
              </div>

              <div>
                <label htmlFor="pb-purpose" className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                  Purpose of Visit
                </label>
                <input id="pb-purpose" name="purposeOfVisit" type="text" value={form.purposeOfVisit}
                  onChange={e => set("purposeOfVisit", e.target.value)}
                  placeholder="Leisure, business, family visit…"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm
                    focus:outline-none focus:ring-2 focus:ring-amber-400" />
              </div>

              {/* Special Requests */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                  <MessageSquare size={12} className="inline mr-1" />
                  Special Requests
                </label>
                <textarea value={form.specialRequests}
                  onChange={e => set("specialRequests", e.target.value)}
                  rows={3} placeholder="Early check-in, extra pillows, ground floor room, dietary requirements…"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm
                    focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none" />
              </div>

              {/* Tenant-defined extra fields — entirely data-driven; a
                  property with none configured renders nothing here at
                  all (customFields is [] by default). */}
              {customFields.map(f => (
                <div key={f.key}>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                    {f.label} {f.required && <span className="text-red-500">*</span>}
                  </label>
                  {f.inputType === "textarea" ? (
                    <textarea rows={3} value={customFieldValues[f.key] || ""}
                      onChange={e => setCustomFieldValues(v => ({ ...v, [f.key]: e.target.value }))}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm
                        focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none" />
                  ) : f.inputType === "select" ? (
                    <select value={customFieldValues[f.key] || ""}
                      onChange={e => setCustomFieldValues(v => ({ ...v, [f.key]: e.target.value }))}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm bg-white
                        focus:outline-none focus:ring-2 focus:ring-amber-400">
                      <option value="">Select…</option>
                      {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : f.inputType === "checkbox" ? (
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input type="checkbox" checked={customFieldValues[f.key] === "true"}
                        onChange={e => setCustomFieldValues(v => ({ ...v, [f.key]: e.target.checked ? "true" : "false" }))}
                        className="w-4 h-4 accent-amber-500" />
                      Yes
                    </label>
                  ) : (
                    <input type={f.inputType === "number" ? "number" : "text"}
                      value={customFieldValues[f.key] || ""}
                      onChange={e => setCustomFieldValues(v => ({ ...v, [f.key]: e.target.value }))}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm
                        focus:outline-none focus:ring-2 focus:ring-amber-400" />
                  )}
                </div>
              ))}

              {/* Multi-file ID proof upload — tenant-controlled visibility (SettingsPage.jsx's BookingFormSection) */}
              {idProofUploadEnabled && (
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                    Identity Proof (Aadhaar / Passport / Driving Licence) *
                  </label>
                  <label className="flex flex-col items-center justify-center border-2 border-dashed
                    border-gray-200 rounded-2xl py-6 cursor-pointer hover:border-amber-400
                    hover:bg-amber-50/30 transition-all group">
                    <Upload size={26} className="text-gray-300 group-hover:text-amber-400 mb-2" />
                    <span className="text-sm text-gray-400 group-hover:text-amber-600 font-medium">
                      Click to upload ID documents
                    </span>
                    <span className="text-xs text-gray-300 mt-1">
                      Required · one ID for each guest ({idsNeeded} needed{idFiles.length ? ` · ${idFiles.length} added` : ""}) · JPG, PNG, PDF · 10MB each
                    </span>
                    <input type="file" className="hidden" multiple accept=".jpg,.jpeg,.png,.pdf"
                      onChange={addFile} />
                  </label>

                  {idFiles.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {idFiles.map((f, i) => (
                        <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2">
                          <Check size={14} className="text-green-500 flex-shrink-0" />
                          <span className="text-xs text-gray-700 flex-1 truncate">{f.name}</span>
                          <span className="text-xs text-gray-400">{(f.size/1024/1024).toFixed(1)}MB</span>
                          <button onClick={() => removeFile(i)}
                            className="p-1 hover:bg-gray-200 rounded-lg transition-colors">
                            <X size={12} className="text-gray-400" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── STEP 2: Review ── */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="bg-gradient-to-br from-gray-50 to-stone-50 rounded-2xl p-5 space-y-3">
                <h3 className="font-bold text-gray-900 text-sm border-b border-gray-200 pb-2 mb-3">
                  Booking Summary
                </h3>
                {selectedRoom && (
                  <div className="bg-amber-50/60 backdrop-blur-md border border-amber-200/60 rounded-xl p-3 mb-3">
                    <div className="flex items-center gap-3">
                      <BedDouble size={18} className="text-amber-600 flex-shrink-0" />
                      <div>
                        <p className="font-bold text-amber-800 text-sm">
                          {selectedRoom.name}{form.roomCount > 1 ? ` × ${form.roomCount} rooms` : ""}
                        </p>
                        <p className="text-xs text-amber-600">
                          {selectedRoom.type} · ₹{(selectedRoom.pricePerNight || 0).toLocaleString("en-IN")}/night base
                        </p>
                      </div>
                      {datesChosen && (
                        <p className="ml-auto font-black text-amber-800">
                          ₹{multiRoomTotal(selectedRoom).toLocaleString("en-IN")}
                        </p>
                      )}
                    </div>
                    {form.roomCount > 1 && (
                      <div className="mt-2.5 pt-2.5 border-t border-amber-200/70 space-y-1">
                        {activeGuestCounts.map((g, i) => (
                          <div key={i} className="flex justify-between text-[11px] text-amber-700">
                            <span>Room {i + 1} — {g} guest{g > 1 ? "s" : ""}</span>
                            <span className="font-semibold">₹{calcRoomTotal(selectedRoom, g, form.checkIn, form.checkOut).toLocaleString("en-IN")}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {[
                  ["Check-in",   new Date(form.checkIn).toLocaleDateString("en-IN", {day:"numeric",month:"long",year:"numeric"})],
                  ["Check-out",  new Date(form.checkOut).toLocaleDateString("en-IN", {day:"numeric",month:"long",year:"numeric"})],
                  ["Duration",   sameDayStay ? "Same-day stay" : `${nights} night${nights!==1?"s":""}`],
                  ...(form.roomCount > 1 ? [["Rooms", `${form.roomCount} rooms`]] : []),
                  ["Guests",     form.roomCount > 1 ? `${totalGuests} total (${activeGuestCounts.join(", ")} per room)` : `${form.guestCount}`],
                  ["Type",       form.bookingType],
                  ["Guest Names",form.bookingType === "Unmarried Couple"
                    ? form.fullNames.filter(Boolean).map((n, i) => `${n} (${form.genders[i] || "?"}, ${form.ages[i] || "?"})`).join(", ")
                    : form.fullNames.filter(Boolean).join(", ")],
                  ["WhatsApp",   form.whatsapp],
                  ["Email",      form.email || "—"],
                  ["Travel From",form.travelFrom || "—"],
                  ["Travel To",  form.travelTo || "—"],
                  ["Purpose",    form.purposeOfVisit || "—"],
                  ["Special Req",form.specialRequests || "None"],
                  ...(idProofUploadEnabled ? [["ID Docs", idFiles.length > 0 ? `${idFiles.length} file${idFiles.length>1?"s":""} uploaded` : "Not uploaded"]] : []),
                ].map(([k,v]) => (
                  <div key={k} className="flex justify-between gap-3 text-sm">
                    <span className="text-gray-400 flex-shrink-0">{k}</span>
                    <span className="font-medium text-gray-900 text-right">{v}</span>
                  </div>
                ))}
              </div>

              {/* Promo code */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                  <Tag size={12} className="inline mr-1" /> Promo Code
                </label>
                <div className="flex gap-2">
                  <input type="text" value={promoInput}
                    onChange={e => { setPromoInput(e.target.value.toUpperCase()); setPromoStatus(null); }}
                    placeholder="Enter code"
                    className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm uppercase
                      focus:outline-none focus:ring-2 focus:ring-amber-400" />
                  <button onClick={applyPromo} disabled={checkingPromo || !promoInput.trim()}
                    className="px-4 py-2.5 rounded-xl bg-gray-900 text-white text-sm font-semibold
                      disabled:opacity-50 hover:bg-gray-800 transition-colors">
                    {checkingPromo ? "…" : "Apply"}
                  </button>
                </div>
                {promoStatus?.valid === false && (
                  <p className="text-xs text-red-600 mt-1.5">{promoStatus.error}</p>
                )}
                {promoStatus?.valid && (
                  <div className="mt-2 bg-green-50 border border-green-200 rounded-xl px-3 py-2 flex justify-between items-center">
                    <span className="text-xs text-green-700 font-medium">
                      {promoStatus.code} applied {promoStatus.type === "Percent" ? `(-${promoStatus.value}%)` : ""}
                    </span>
                    <span className="text-sm font-black text-green-800">-₹{promoStatus.discount.toLocaleString("en-IN")}</span>
                  </div>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-100 rounded-2xl p-3">
                <p className="text-xs text-blue-600">
                  <strong>Note:</strong> This is a booking <em>request</em>. Our team will confirm
                  via WhatsApp and provide payment details.
                </p>
              </div>


              {/* Only rendered (and only enforced server-side) when the
                  tenant has actually written a policy — a property with
                  none set has nothing to ask a guest to agree to. */}
              {guestPolicyText && (
                <div className="border border-gray-200 rounded-2xl p-4 space-y-3">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Guest Policy &amp; Terms and Conditions
                  </p>
                  <div className="max-h-40 overflow-y-auto text-xs text-gray-600 whitespace-pre-line
                    bg-gray-50 rounded-xl p-3 leading-relaxed">
                    {guestPolicyText}
                  </div>
                  <label className="flex items-start gap-2.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={policyAccepted}
                      onChange={e => setPolicyAccepted(e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-amber-500 flex-shrink-0"
                    />
                    <span className="text-xs text-gray-700">
                      I have read and agree to the Guest Policy &amp; Terms and Conditions above.
                    </span>
                  </label>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-2xl px-4 py-3 flex items-start gap-2">
              <AlertTriangle size={15} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-600 whitespace-pre-line">{error}</p>
            </div>
          )}

          {/* Navigation */}
          <div className="flex gap-3 mt-7">
            {step > 0 && (
              <button onClick={() => { setStep(s => s - 1); setError(""); }}
                className="flex items-center gap-1.5 px-5 py-3 rounded-2xl border border-gray-200
                  text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors">
                <ChevronLeft size={16} /> Back
              </button>
            )}
            {step < 2 ? (
              <button onClick={tryNext}
                className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl
                  bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold text-sm
                  hover:from-amber-600 hover:to-orange-600 shadow-md transition-all">
                Continue <ChevronRight size={16} />
              </button>
            ) : (
              <button onClick={submit} disabled={submitting || (!!guestPolicyText && !policyAccepted)}
                title={!!guestPolicyText && !policyAccepted ? "Please accept the Guest Policy & Terms and Conditions above" : undefined}
                className="flex-1 py-3.5 rounded-2xl bg-gradient-to-r from-green-500 to-emerald-500
                  text-white font-bold text-sm hover:from-green-600 hover:to-emerald-600
                  shadow-md transition-all disabled:opacity-60">
                {submitting
                  ? (mustPayToBook ? "Opening payment…" : "Submitting…")
                  : mustPayToBook
                    ? `Pay ₹${Math.max(0, multiRoomTotal(selectedRoom) - (promoStatus?.valid ? promoStatus.discount : 0)).toLocaleString("en-IN")} & Confirm`
                    : "✓ Confirm Booking Request"}
              </button>
            )}
          </div>
        </div>
      </div>


        {/* Cross-promotion ad (optional, hotel-specific) */}
        {crossPromoName && crossPromoUrl && (
        <a href={crossPromoUrl} target="_blank" rel="noopener noreferrer"
          className="block mt-4 group">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r
            from-gray-900 to-gray-800 p-4 shadow-xl hover:shadow-2xl
            transition-all duration-300 transform hover:-translate-y-0.5">
            {/* Glowing border */}
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-amber-500/20
              to-orange-500/20 opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500
                flex items-center justify-center flex-shrink-0 shadow-md
                group-hover:scale-110 transition-transform duration-300">
                <span className="text-2xl">✈️</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-widest text-amber-400 mb-0.5">
                  Also planning a trip?
                </p>
                <p className="font-black text-white text-sm leading-tight">
                  Explore Northeast India with{" "}
                  <span className="text-amber-400">{crossPromoName}</span>
                </p>
                <p className="text-gray-400 text-[11px] mt-0.5">
                  Kaziranga · Meghalaya · Arunachal · Sikkim tours
                </p>
              </div>
              <div className="flex-shrink-0">
                <span className="text-xs font-bold text-white bg-gradient-to-r
                  from-amber-500 to-orange-500 px-3 py-2 rounded-xl
                  group-hover:from-amber-400 group-hover:to-orange-400 transition-all">
                  Explore →
                </span>
              </div>
            </div>
          </div>
        </a>
        )}

      {showAgeModal && (
        <AgeModal
          onConfirm={() => { set("ageConfirmed", true); setShowAgeModal(false); setStep(1); }}
          onCancel={() => { set("bookingType", "Standard"); setShowAgeModal(false); }}
        />
      )}
    </div>
  );
}
